import { useEffect, useRef, useSyncExternalStore } from "react";
import {
    acquireConnection,
    addMessageHandler,
    getBrokerStatus,
    publishMessage,
    subscribeBrokerStatus,
    subscribeTopics,
} from "../services/mqtt/mqtt";
import { MQTT_PREFIX } from "../services/mqtt/mqttconfig";
import { isSpaActivity, nextLivenessAction, SPA_WATCHDOG_TICK_MS } from "../services/mqtt/spaLiveness";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../redux/states/root.state";
import { setLiveData, resetLiveData } from "../redux/reducers/live.reducer";
import { setSettingsData, resetSettingsData } from "../redux/reducers/settings.reducer";
import { AppDispatch } from "../redux/store";
import { setConnected, setCurrentSpa, setDisconnected, setConnectingFinished } from "../redux/reducers/spa.reducer";
import { setConstData, resetConstData } from "../redux/reducers/const.reducer";
import { setErrorData, resetErrorData } from "../redux/reducers/error.reducer";
import { setStatusData } from "../redux/reducers/status.reducer";
import { setUpdateStatusData, resetUpdateStatusData } from "../redux/reducers/updateStatus.reducer";

const defaultDealerId = process.env.REACT_APP_DEFAULT_DEALER_ID??'44';
const mqttPrefix = MQTT_PREFIX;

// Sections of the consolidated `/state` payload that we mirror into redux. Each one has a
// matching standalone topic (`.../live`, `.../sett`, ...) which always takes precedence.
const STATE_SECTIONS = ["live", "sett", "const", "error", "status"] as const;
type StateSection = typeof STATE_SECTIONS[number];

// Spa timestamps as epoch milliseconds, or null if missing or unreadable. The firmware may
// send epoch seconds, epoch milliseconds or a date string.
const parseSpaTimestamp = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    const numeric = Number(value);
    const ms = Number.isFinite(numeric)
        ? (numeric < 1e12 ? numeric * 1000 : numeric)
        : new Date(value as string).getTime();
    return Number.isFinite(ms) && ms > 0 ? ms : null;
};

// How long to show "connecting" before showing the spa as offline. Polling carries on.
const CONNECTING_TIMEOUT_MS = 4000;
// Status polling for a spa that isn't connected: 100ms, 1600ms, 3100ms, ... up to the cap.
const OFFLINE_POLL_START_MS = 100;
const OFFLINE_POLL_STEP_MS = 1500;
const OFFLINE_POLL_MAX_MS = 30_000;
// How long the broker connection can be down before the spa is shown as offline.
const BROKER_DOWN_GRACE_MS = 10_000;

const useMQTT = (spaId: string) => {
    const email = useSelector((state: RootState) => state.user.data?.email ?? null);
    const dealerId = useSelector((state: RootState) => state.spa.data?.dealership_id) ?? defaultDealerId;
    const isConnected = useSelector((state: RootState) =>
        state.spa.currentSpaId === spaId ? state.spa.isCurrentSpaConnected : false
    );
    const brokerConnected = useSyncExternalStore(subscribeBrokerStatus, getBrokerStatus) === "connected";
    const dispatch: AppDispatch = useDispatch();

    // Hold on to the email so a transient null (e.g. user data being refetched) doesn't
    // drop the connection.
    const emailRef = useRef<string | null>(email);
    if (email) emailRef.current = email;
    const connectEmail = emailRef.current;

    // Read inside timers and message handlers, which outlive the render they were created in.
    const isConnectedRef = useRef(isConnected);
    useEffect(() => {
        isConnectedRef.current = isConnected;
    }, [isConnected]);

    const topicBase = spaId ? `${mqttPrefix}/${dealerId}/${spaId}` : "";
    const commandTopic = topicBase ? `${topicBase}/command` : "";

    // Keep the shared connection open while this component is mounted.
    useEffect(() => {
        if (!connectEmail) return;
        return acquireConnection(connectEmail);
    }, [connectEmail]);

    // Select the spa, clearing the previous spa's data so the UI doesn't show stale info.
    const prevSpaIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (!spaId) {
            prevSpaIdRef.current = null;
            return;
        }
        if (prevSpaIdRef.current && prevSpaIdRef.current !== spaId) {
            console.log(`[useMQTT] Spa changed from ${prevSpaIdRef.current} to ${spaId}, clearing cached data`);
            dispatch(resetLiveData());
            dispatch(resetSettingsData());
            dispatch(resetConstData());
            dispatch(resetErrorData());
            dispatch(resetUpdateStatusData());
            dispatch(setStatusData({}));
        }
        prevSpaIdRef.current = spaId;
        dispatch(setCurrentSpa(spaId));
    }, [spaId, dispatch]);

    // Stop showing "connecting" if the spa hasn't reported in shortly after we (re)connect.
    useEffect(() => {
        if (!spaId) return;
        const timeoutId = setTimeout(() => {
            if (!isConnectedRef.current) {
                console.warn('[useMQTT] Spa connection-status not received in time');
                dispatch(setConnectingFinished(spaId));
            }
        }, CONNECTING_TIMEOUT_MS);
        return () => clearTimeout(timeoutId);
    }, [spaId, brokerConnected, dispatch]);

    // Topics, message handling and spa liveness. The connection module re-applies the topics
    // and handler after every reconnect, so this only re-runs when the spa or its topics change.
    useEffect(() => {
        if (!topicBase) return;

        const connectionStatusTopic = `${topicBase}/connection-status`;
        const topics = [
            `${topicBase}/live`,
            `${topicBase}/sett`,
            `${topicBase}/const`,
            `${topicBase}/error`,
            `${topicBase}/status`,
            `${topicBase}/state`,
            `${topicBase}/update-status`,
            `${topicBase}/command`,
            connectionStatusTopic,
            `$aws/events/presence/disconnected/${spaId}`,
        ];

        // Spa liveness: only messages the spa itself just published count (see isSpaActivity).
        // `probeSentAt` is set while a status request is unanswered.
        let lastSpaActivityAt = Date.now();
        let probeSentAt: number | null = null;

        // What was last applied for each section during this subscription.
        const appliedSections: Partial<Record<StateSection, { fromState: boolean; timestamp: number | null }>> = {};

        // Route one section of spa data to its reducer, unless what's already there is newer.
        // `fromState` marks data from the consolidated `/state` topic. When both sides have a
        // timestamp the newer one wins, so a long-outdated retained `/live` can't replace a
        // fresher `/state` (and a tie goes to the dedicated topic). Without timestamps to
        // compare, the dedicated topic takes precedence as before.
        const applySection = (section: StateSection, data: any, fromState: boolean, timestamp: number | null) => {
            const applied = appliedSections[section];
            if (applied) {
                if (timestamp !== null && applied.timestamp !== null) {
                    if (timestamp < applied.timestamp) return;
                    if (timestamp === applied.timestamp && fromState && !applied.fromState) return;
                } else if (fromState && !applied.fromState) {
                    return;
                }
            }
            appliedSections[section] = { fromState, timestamp };

            switch (section) {
                case "live":
                    dispatch(setLiveData(data));
                    break;
                case "sett":
                    dispatch(setSettingsData(data));
                    break;
                case "const":
                    dispatch(setConstData(data));
                    break;
                case "error":
                    dispatch(setErrorData(data));
                    break;
                case "status":
                    dispatch(setStatusData(data));
                    break;
            }
        };

        const handleMqttMessages = (topic: string, message: Buffer, packet: { retain?: boolean }) => {
            const payload = message.toString();
            const topicParts = topic.split("/");
            // Retained messages are replayed by the broker on subscribe, so they say nothing
            // about whether the spa is online now.
            const retained = Boolean(packet?.retain);

            // MQTT-only: AWS presence disconnect ($aws/events/presence/disconnected/{clientId})
            // Handle before topicSpaId filter — for that topic, topicParts[2] is "presence", not the spa id
            if (topic.startsWith("$aws/events/presence/disconnected/")) {
                try {
                    const data = JSON.parse(payload);
                    if (data.eventType === "disconnected") {
                        const clientIdFromTopic = topicParts[topicParts.length - 1];
                        if (clientIdFromTopic === spaId) {
                            dispatch(setDisconnected(spaId));
                        }
                    }
                } catch (e) {
                    console.error("[useMQTT] Failed to parse presence disconnect payload", e);
                }
                return;
            }

            // Strictly match spaId in topic path (e.g. beta-as/44/<spaId>/live)
            const topicSpaId = topicParts.length > 2 ? topicParts[2] : undefined;
            if (topicSpaId !== spaId) return; // Only process/log for this spa
            const topicName = topicParts[topicParts.length - 1];
            if (isSpaActivity(topicName, payload, retained)) {
                lastSpaActivityAt = Date.now();
                probeSentAt = null;
            }
            try {
                if (STATE_SECTIONS.includes(topicName as StateSection)) {
                    const data = JSON.parse(payload);
                    applySection(topicName as StateSection, data, false, parseSpaTimestamp(data?.timestamp));
                }
                // Consolidated retained topic (Yocto 3.1.74+). Fills in sections the dedicated
                // topics haven't delivered, or replaces ones older than it — see applySection.
                if (topicName === "state") {
                    const data = JSON.parse(payload);
                    STATE_SECTIONS.forEach(section => {
                        const sectionData = data?.[section];
                        if (!sectionData || typeof sectionData !== "object") return;
                        const timestamp = parseSpaTimestamp(sectionData.timestamp ?? data.timestamp);
                        // `/state` carries the timestamp once at the top level, whereas the
                        // standalone `.../live` payload carries it inside itself — which is where
                        // the UI reads it from (e.g. "Spa Time"). Graft it on so the fallback
                        // isn't missing a spa time until the first live packet arrives.
                        if (section === "live" && sectionData.timestamp === undefined && data.timestamp !== undefined) {
                            applySection(section, { ...sectionData, timestamp: data.timestamp }, true, timestamp);
                            return;
                        }
                        applySection(section, sectionData, true, timestamp);
                    });
                }
                // A retained status can be left over from before the spa went offline; only a
                // live report changes the connection state.
                if (topicName === "connection-status" && !retained) {
                    const data = JSON.parse(payload);
                    if (data.connection_status === "connected") {
                        dispatch(setConnected(spaId));
                    } else if (data.connection_status === "disconnected") {
                        dispatch(setDisconnected(spaId));
                    }
                }
                if (topicName === "update-status") {
                    if (payload.trim() === "{}") {
                        dispatch(setUpdateStatusData({}));
                        return;
                    }
                    dispatch(setUpdateStatusData(JSON.parse(payload)));
                }

            } catch (error) {
                console.error("Failed to parse JSON:", error);
            }
        };

        const removeHandler = addMessageHandler(handleMqttMessages);
        const unsubscribeTopics = subscribeTopics(topics);

        const requestConnectionStatus = () => {
            publishMessage(
                connectionStatusTopic,
                JSON.stringify({ yocto_command: "report_connection_status" }),
                false
            );
        };

        // Watchdog. It only runs while the broker connection is up; while it's down,
        // watchBrokerDown below decides what the UI shows.
        let pollDelay = OFFLINE_POLL_START_MS;
        let watchdogTimeoutId: ReturnType<typeof setTimeout> | null = null;
        const schedule = (ms: number) => {
            if (watchdogTimeoutId) clearTimeout(watchdogTimeoutId);
            watchdogTimeoutId = setTimeout(checkSpa, ms);
        };
        const checkSpa = () => {
            watchdogTimeoutId = null;
            if (getBrokerStatus() !== "connected") return; // resumed by the status listener

            if (isConnectedRef.current) {
                // Reset so polling restarts from the fast end if the spa drops
                pollDelay = OFFLINE_POLL_START_MS;

                // The spa can't tell us it went offline, so silence is the signal: ask for its
                // status after a quiet spell, and mark it offline if it doesn't answer.
                const action = nextLivenessAction(Date.now(), lastSpaActivityAt, probeSentAt);
                if (action === "probe") {
                    requestConnectionStatus();
                    probeSentAt = Date.now();
                } else if (action === "offline") {
                    console.warn(`[useMQTT] Spa ${spaId} didn't answer a status request, marking it offline`);
                    probeSentAt = null;
                    // Update the ref now rather than after the re-render, so the next tick goes
                    // straight to offline polling.
                    isConnectedRef.current = false;
                    dispatch(setDisconnected(spaId));
                }
                schedule(SPA_WATCHDOG_TICK_MS);
                return;
            }

            requestConnectionStatus();
            schedule(pollDelay);
            pollDelay = Math.min(pollDelay + OFFLINE_POLL_STEP_MS, OFFLINE_POLL_MAX_MS);
        };

        // Ask the spa for its status right away instead of waiting for the next check. Used
        // after a reconnect, when whatever the spa sent in the meantime was lost, and when the
        // user comes back to the tab and needs to know whether the controls will work.
        const checkSpaNow = () => {
            pollDelay = OFFLINE_POLL_START_MS;
            if (isConnectedRef.current && getBrokerStatus() === "connected") {
                if (probeSentAt === null) {
                    requestConnectionStatus();
                    probeSentAt = Date.now();
                }
                schedule(SPA_WATCHDOG_TICK_MS);
                return;
            }
            schedule(OFFLINE_POLL_START_MS);
        };

        // Without the broker there's no way to reach the spa, so don't keep showing it as
        // online. The grace period covers quick reconnects such as a token refresh.
        let brokerDownTimeoutId: ReturnType<typeof setTimeout> | null = null;
        const watchBrokerDown = () => {
            if (brokerDownTimeoutId) return;
            brokerDownTimeoutId = setTimeout(() => {
                brokerDownTimeoutId = null;
                if (getBrokerStatus() === "connected" || !isConnectedRef.current) return;
                console.warn(`[useMQTT] Lost the broker connection, showing spa ${spaId} as offline`);
                isConnectedRef.current = false;
                probeSentAt = null;
                dispatch(setDisconnected(spaId));
            }, BROKER_DOWN_GRACE_MS);
        };

        let wasBrokerConnected = getBrokerStatus() === "connected";
        if (!wasBrokerConnected) watchBrokerDown();
        const unsubscribeStatus = subscribeBrokerStatus(() => {
            const nowConnected = getBrokerStatus() === "connected";
            if (nowConnected && !wasBrokerConnected) {
                if (brokerDownTimeoutId) clearTimeout(brokerDownTimeoutId);
                brokerDownTimeoutId = null;
                checkSpaNow();
            } else if (!nowConnected) {
                watchBrokerDown();
            }
            wasBrokerConnected = nowConnected;
        });
        const handleVisibility = () => {
            if (document.visibilityState === "visible") checkSpaNow();
        };
        document.addEventListener("visibilitychange", handleVisibility);

        schedule(OFFLINE_POLL_START_MS);

        return () => {
            if (watchdogTimeoutId) clearTimeout(watchdogTimeoutId);
            if (brokerDownTimeoutId) clearTimeout(brokerDownTimeoutId);
            unsubscribeStatus();
            document.removeEventListener("visibilitychange", handleVisibility);
            removeHandler();
            unsubscribeTopics();
        };
    }, [topicBase, spaId, dispatch]);

    return [commandTopic];
};

export default useMQTT;
